import React, { useState, useEffect } from 'react';
import SearchForm from './components/SearchForm';
import RepoList from './components/RepoList';
import { getStarredRepos, filterRepos, getStarredReposCount, validateToken } from './services/githubService';

function App() {
  const [allRepos, setAllRepos] = useState([]);
  const [filteredRepos, setFilteredRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState('');
  const [totalStars, setTotalStars] = useState(0);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const [tokenStatus, setTokenStatus] = useState({
    isValid: false,
    message: '',
  });
  const [loadingProgress, setLoadingProgress] = useState({
    loaded: 0,
    total: 0,
    currentPage: 0,
    isComplete: false
  });
  const [isSemanticSearch, setIsSemanticSearch] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // 当组件加载时，从 localStorage 获取缓存的用户名，但不自动执行搜索
  useEffect(() => {
    const cachedUser = localStorage.getItem('github_username');
    if (cachedUser) {
      setCurrentUser(cachedUser);
      // 不自动执行搜索，只设置用户名
      // 确保allRepos为空，避免显示表头
      setAllRepos([]);
      setFilteredRepos([]);
      setTotalStars(0);
    }
  }, []);

  // 当filteredRepos变化时，更新搜索结果数量
  useEffect(() => {
    setSearchResultCount(filteredRepos.length);
  }, [filteredRepos]);

  const handleProgress = (loaded, total, currentPage) => {
    setLoadingProgress({
      loaded,
      total,
      currentPage,
      isComplete: loaded >= total
    });
  };

  const handleSearch = async (username, token = '', tokenValidStatus = false) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setLoadingProgress({
      loaded: 0,
      total: 0,
      currentPage: 0,
      isComplete: false
    });
    
    try {
      // 使用传入的令牌状态，而不是重复验证
      if (token) {
        if (tokenValidStatus) {
          setTokenStatus({
            isValid: true,
            message: '✅ GitHub令牌有效，API请求不受限制'
          });
        } else {
          setTokenStatus({
            isValid: false,
            message: '❌ GitHub令牌无效，将使用未认证的API请求（受限制）'
          });
          // 继续执行，但不使用令牌
          token = '';
        }
      } else {
        setTokenStatus({
          isValid: false,
          message: '⚠️ 未提供GitHub令牌，API请求可能受限制'
        });
      }
      
      // 获取star仓库列表，传入进度回调函数和可选的token
      const repos = await getStarredRepos(username, 100, handleProgress, token);
      
      setAllRepos(repos);
      
      // 直接显示所有仓库，不应用过滤
      setFilteredRepos(repos);
      setSearchResultCount(repos.length);
      
      setCurrentUser(username);
      setTotalStars(repos.length);
    } catch (err) {
      console.error('搜索错误:', err);
      setError(err);
      setAllRepos([]);
      setFilteredRepos([]);
      setTotalStars(0);
      setSearchResultCount(0);
    } finally {
      setLoading(false);
      setLoadingProgress(prev => ({
        ...prev,
        isComplete: true
      }));
    }
  };

  const handleFilter = async (keyword, useSemanticSearch = false, apiKey = '', threshold = 0.7, cachedResults = null, includeReadme = false) => {
    if (allRepos.length === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      setIsSemanticSearch(useSemanticSearch);
      
      // 如果提供了缓存的结果，直接使用缓存结果进行过滤
      if (cachedResults && Array.isArray(cachedResults)) {
        let filtered;
        if (useSemanticSearch) {
          // 语义搜索模式：根据阈值过滤
          filtered = cachedResults.filter(item => item.similarity >= threshold);
        } else {
          // 关键字搜索模式：只显示相似度大于0的结果
          filtered = cachedResults.filter(item => item.similarity > 0);
        }
        setFilteredRepos(filtered);
        setSearchResultCount(filtered.length);
        return cachedResults; // 返回完整的缓存结果，以便在UI中保存
      } else {
        // 否则执行新的搜索
        // 获取当前的token
        const token = localStorage.getItem('github_token') || '';
        
        // 将API密钥、token和includeReadme参数传递给filterRepos函数
        const filtered = await filterRepos(allRepos, keyword, useSemanticSearch, apiKey, threshold, token, includeReadme);
        const filteredResults = useSemanticSearch 
          ? filtered.filter(item => item.similarity >= threshold) 
          : filtered.filter(item => item.similarity > 0);
        
        setFilteredRepos(filteredResults);
        setSearchResultCount(filteredResults.length);
        return filtered; // 返回结果，以便在UI中缓存
      }
    } catch (error) {
      console.error('过滤仓库时出错:', error);
      setError('过滤仓库时出错: ' + error.message);
      setSearchResultCount(0);
      return []; // 出错时返回空数组
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <header className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">GitHub Star 管理器</h1>
        <p className="text-gray-600">查看、搜索和管理您在GitHub上Star的仓库</p>
      </header>
      
      <SearchForm onSearch={handleSearch} onFilter={handleFilter} />
      
      {currentUser && !loading && !error && allRepos.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            用户 <span className="text-blue-600">{currentUser}</span> 的Star仓库
            <span className="ml-2 bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
              总计: {totalStars} 个仓库
            </span>
            {searchResultCount !== totalStars && (
              <span className="ml-2 bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded">
                搜索结果: {searchResultCount} 个仓库
              </span>
            )}
          </h2>
          
          {tokenStatus.message && (
            <div className={`mt-3 p-3 rounded-md ${tokenStatus.isValid ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'}`}>
              {tokenStatus.message}
            </div>
          )}
        </div>
      )}
      
      {loading && loadingProgress.total > 0 && (
        <div className="mb-6 bg-blue-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-blue-800">正在加载 {currentUser} 的 Star 仓库</h3>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                style={{ width: `${Math.min(100, (loadingProgress.loaded / loadingProgress.total) * 100)}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-1 text-sm text-gray-600">
              <span>已加载: {loadingProgress.loaded} / {loadingProgress.total}</span>
              <span>当前页: {loadingProgress.currentPage}</span>
            </div>
          </div>
        </div>
      )}
      
      <RepoList 
        repos={filteredRepos} 
        loading={loading} 
        error={error}
        useSemanticSearch={isSemanticSearch}
        hasSearched={hasSearched}
      />
    </div>
  );
}

export default App; 